'use client';

import {
  IcLikeFilled,
  IcLikeOpacity,
  Image,
  LikeMotion,
  RankingLabel,
  Typography,
} from '@musinsa/mds';
import { SERVICE, useDeviceInfo } from '@musinsa/packages-device-detection';
import clsx from 'clsx';
import Link from 'next/link';
import { type MouseEvent, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { useProductLikeMutation } from '../../common/hooks/useProductLikeMutation';

interface Props {
  rank: number;
  imageUrl: string;
  brandName: string;
  goodsName: string;
  price: number;
  totalQuantity: number;
  totalCommission: number;
  linkUrl: string;
  goodsNo: number;
  discountRate?: number | null;
  initialIsLiked?: boolean;
  className?: string;
}

export const ProductRankingCarouselCard = ({
  rank,
  imageUrl,
  brandName,
  goodsName,
  price,
  totalQuantity,
  totalCommission,
  linkUrl,
  goodsNo,
  discountRate,
  initialIsLiked = false,
  className,
}: Props) => {
  const { service } = useDeviceInfo();
  const is29cm = service === SERVICE['29CM'];
  const [isLiked, setIsLiked] = useState(initialIsLiked);
  const { mutate } = useProductLikeMutation({
    onError: () => setIsLiked((prev) => !prev),
  });

  const handleToggleLike = (e: MouseEvent<SVGSVGElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const next = !isLiked;
    setIsLiked(next);
    mutate({ goodsNo: String(goodsNo), isLiked: next });
  };

  return (
    <Link href={linkUrl} className={twMerge('block shrink-0 w-[109px]', className)}>
      {/* 상품 썸네일 비율 — 29CM=square(5:5, 109x109) / 무신사=portrait(109x131). 전 지면 공통 정책. (SID-4424) */}
      <div
        className={clsx(
          'relative w-[109px] overflow-hidden bg-gray-100',
          is29cm ? 'h-[109px]' : 'h-[131px]',
        )}
      >
        <Image
          src={imageUrl}
          alt={goodsName}
          objectFit="cover"
          size="cover"
          direction="horizontal"
          heightFull
        />
        <div className="absolute inset-0 bg-black/[0.05] pointer-events-none" />
        <div className="absolute inset-0 bg-black/[0.02] pointer-events-none" />
        <div className="absolute top-[8px] left-[8px] z-10 flex">
          <RankingLabel>{rank}</RankingLabel>
        </div>
        <div className="absolute z-10 right-[4px] bottom-[4px] w-[20px] h-[20px] flex items-center justify-center">
          <LikeMotion like={isLiked}>
            {isLiked ? (
              <IcLikeFilled
                fillColor="red"
                width={20}
                height={20}
                strokeColor="transparent"
                onClick={handleToggleLike}
              />
            ) : (
              <IcLikeOpacity
                fillColor="gray-500"
                fillOpacity="0.6"
                width={20}
                height={20}
                strokeColor="white"
                onClick={handleToggleLike}
              />
            )}
          </LikeMotion>
        </div>
      </div>
      <div className="pt-[8px]! pb-[12px]! pl-[8px]! pr-[4px]! flex flex-col">
        <Typography variant="textEtc11pxSemibold" lineClamp="one" className="mb-[2px]!">
          {brandName}
        </Typography>
        <Typography variant="body12pxReg" lineClamp="two" className="mb-[1px]!">
          {goodsName}
        </Typography>
        <div className="flex items-baseline gap-[2px] mb-[4px]!">
          {discountRate ? (
            <Typography variant="body13pxSemi" colorScheme="red">
              {discountRate}%
            </Typography>
          ) : null}
          <Typography variant="body13pxSemi">{price.toLocaleString()}원</Typography>
        </div>
        <div className="flex flex-wrap gap-x-[2px]">
          <Typography variant="textEtc11pxReg" colorScheme="gray-500">
            {totalQuantity}건 판매 /
          </Typography>
          <Typography variant="textEtc11pxReg" colorScheme="gray-500">
            {totalCommission.toLocaleString()}원
          </Typography>
        </div>
      </div>
    </Link>
  );
};
